import { useEffect, useState } from 'react';

import { SafeElectronAPI } from 'src/hooks/useElectron';

export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'downloading'
  | 'ready'
  | 'up_to_date';

interface UpdateState {
  status: UpdateStatus;
  lastCheckedAt: Date | null;
}

let state: UpdateState = { status: 'idle', lastCheckedAt: null };
const listeners = new Set<() => void>();

let cachedApi: SafeElectronAPI | null = null;

function setState(next: Partial<UpdateState>) {
  state = { ...state, ...next };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Called once from ElectronIntegration to wire up IPC listeners.
 * Returns a cleanup function to unsubscribe.
 */
export function initElectronUpdateStore(api: SafeElectronAPI) {
  cachedApi = api;

  const unsubAvailable = api.onUpdateAvailable(() => {
    setState({ status: 'downloading' });
  });

  const unsubDownloaded = api.onUpdateDownloaded(() => {
    setState({ status: 'ready' });
  });

  return () => {
    unsubAvailable();
    unsubDownloaded();
    cachedApi = null;
  };
}

export async function checkForUpdates() {
  if (!cachedApi) return;

  setState({ status: 'checking' });
  try {
    const updateInfo = await cachedApi.checkForUpdates();
    setState({
      lastCheckedAt: new Date(),
      status: updateInfo ? 'downloading' : 'up_to_date',
    });
  } catch (error) {
    console.error('Failed to check for updates', error);
    setState({ lastCheckedAt: new Date(), status: 'up_to_date' });
  }
}

export function useElectronUpdateStatus(): UpdateState {
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    // Sync in case state changed between render and effect
    setSnapshot(state);
    return subscribe(() => setSnapshot(state));
  }, []);

  return snapshot;
}
